import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { InputLabel } from '@/components/ui/InputLabel';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import Title from '@/components/ui/Title';
import { Plus, X } from 'lucide-react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { selectUsage } from '@/features/billing/billingSlice';
import { fetchUsage } from '@/features/billing/billingThunks';
import {
  fetchWorkspaceTeam,
  removeWorkspaceTeamMember,
  transferWorkspaceOwnership,
  updateWorkspaceTeamMemberRole,
} from '@/features/workspace/workspaceThunks';
import type { WorkspaceRole, WorkspaceTeamMember } from '@/features/workspace/types';
import { ASSIGNABLE_WORKSPACE_ROLE, normalizeWorkspaceRole } from '@/lib/workspaceRole';
import EditIcon from '@/components/icons/EditIcon';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { createWorkspaceInvitation } from '@/features/webinar/webinarThunks';
import LimitUpsellDialog from '@/components/billing/LimitUpsellDialog';
import { toast } from 'react-toastify';
import { useSubmitLock } from '@/hooks/useSubmitLock';

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  role: WorkspaceRole;
};

export default function TeamPage() {
  const { t } = useTranslation('team');
  const { t: tCommon } = useTranslation('common');
  const dispatch = useAppDispatch();
  const currentWorkspace = useAppSelector((state: RootState) => state.workspace.currentWorkspace);
  const currentUser = useAppSelector((state: RootState) => state.auth.user);
  const teamActionLoading = useAppSelector((state: RootState) => state.workspace.teamActionLoading);
  const teamMeta = useAppSelector((state: RootState) => state.workspace.teamMeta);
  const team = useAppSelector((state: RootState) => state.workspace.team);
  const isWelyaUser = Boolean(currentUser?.email?.toLowerCase().endsWith('@welya.io'));
  const usage = useAppSelector(selectUsage);
  const teamMemberUsage = usage?.teamMembers;
  const isAtMax = Boolean(teamMemberUsage && teamMemberUsage.current >= teamMemberUsage.max);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<WorkspaceTeamMember | null>(null);
  const [pictureFile, setPictureFile] = useState<File | null>(null);
  const [picturePreview, setPicturePreview] = useState<string | null>(null);
  const pictureInputRef = useRef<HTMLInputElement>(null);

  const schema = useMemo(
    () =>
      z.object({
        firstName: z
          .string()
          .min(1, t('validation.firstNameRequired'))
          .max(25, t('validation.firstNameMax'))
          .refine((val) => val.trim().length > 0, {
            message: t('validation.firstNameRequired'),
          }),
        lastName: z
          .string()
          .min(1, t('validation.lastNameRequired'))
          .max(25, t('validation.lastNameMax'))
          .refine((val) => val.trim().length > 0, {
            message: t('validation.lastNameRequired'),
          }),
        email: z
          .email(t('validation.emailInvalid'))
          .max(200, t('validation.emailMax'))
          .refine((val) => val.trim().length > 0, {
            message: t('validation.emailInvalid'),
          }),
        role: z.enum(['ADMIN', 'USER']),
      }),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      role: ASSIGNABLE_WORKSPACE_ROLE,
    },
    mode: 'onChange',
  });

  const editRoleForm = useForm<{ role: WorkspaceRole }>({
    resolver: zodResolver(z.object({ role: z.enum(['ADMIN', 'USER']) })),
    defaultValues: {
      role: ASSIGNABLE_WORKSPACE_ROLE,
    },
    mode: 'onChange',
  });

  const isValid = form.formState.isValid;
  const { isLocked: isInviteLocked, runExclusive: runInviteExclusive } = useSubmitLock();
  const isSubmitting = form.formState.isSubmitting || teamActionLoading || isInviteLocked;

  useEffect(() => {
    if (currentWorkspace?.id) {
      dispatch(
        fetchWorkspaceTeam({
          workspaceId: currentWorkspace.id,
          page: 0,
          size: 20,
        }),
      );
    }
  }, [currentWorkspace?.id, dispatch]);

  useEffect(() => {
    if (team.length > 0) {
      console.log("Membres de l'équipe:", team);
      console.log("Métadonnées de l'équipe:", teamMeta);
    }
  }, [team, teamMeta]);

  useEffect(() => {
    if (!sheetOpen) {
      form.reset();
      setPictureFile(null);
      setPicturePreview(null);
    }
  }, [sheetOpen, form]);

  const handlePictureSelect = useCallback(
    (file: File) => {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        toast.error(t('picture.invalidFormat'));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t('picture.tooLarge'));
        return;
      }
      setPictureFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPicturePreview(reader.result as string);
      reader.readAsDataURL(file);
    },
    [t],
  );

  useEffect(() => {
    if (selectedMember) {
      editRoleForm.reset({ role: normalizeWorkspaceRole(selectedMember.role) });
    }
  }, [selectedMember, editRoleForm]);

  const handleEditClick = (member: WorkspaceTeamMember) => {
    setSelectedMember(member);
    setEditDialogOpen(true);
  };

  const onEditRoleSubmit = async (data: { role: WorkspaceRole }) => {
    if (!currentWorkspace?.id || !selectedMember) return;

    try {
      const result = await dispatch(
        updateWorkspaceTeamMemberRole({
          workspaceId: currentWorkspace.id,
          userId: selectedMember.userId,
          page: teamMeta?.number ?? 0,
          size: teamMeta?.size ?? 20,
          input: {
            role: data.role,
          },
        }),
      );

      if (updateWorkspaceTeamMemberRole.fulfilled.match(result)) {
        setEditDialogOpen(false);
        setSelectedMember(null);
      }
      if (updateWorkspaceTeamMemberRole.rejected.match(result)) {
        console.log(result.payload?.message);

        toast.error(tCommon(result.payload?.message ?? 'errors.generic'));
      }
    } catch (err) {
      console.error('Failed to update team member role:', err);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const onTransferOwner = async () => {
    if (!currentWorkspace?.id || !selectedMember || selectedMember.creator) return;

    try {
      const result = await dispatch(
        transferWorkspaceOwnership({
          workspaceId: currentWorkspace.id,
          newOwnerUserIdentifier: selectedMember.userId,
          page: teamMeta?.number ?? 0,
          size: teamMeta?.size ?? 20,
        }),
      );

      if (transferWorkspaceOwnership.fulfilled.match(result)) {
        dispatch(
          fetchWorkspaceTeam({
            workspaceId: currentWorkspace.id,
            page: teamMeta?.number ?? 0,
            size: teamMeta?.size ?? 20,
          }),
        );

        toast.success(t('ownerTransferSuccess'));
        setEditDialogOpen(false);
        setSelectedMember(null);
      }
    } catch (err) {
      console.error('Failed to transfer ownership:', err);
    }
  };

  const onDeleteConfirm = async () => {
    if (!currentWorkspace?.id || !selectedMember) return;

    try {
      const result = await dispatch(
        removeWorkspaceTeamMember({
          workspaceId: currentWorkspace.id,
          userId: selectedMember.userId,
          page: teamMeta?.number ?? 0,
          size: teamMeta?.size ?? 20,
        }),
      );

      if (removeWorkspaceTeamMember.fulfilled.match(result)) {
        setDeleteDialogOpen(false);
        setEditDialogOpen(false);
        setSelectedMember(null);
        dispatch(fetchUsage(currentWorkspace.id));
      }

      if (removeWorkspaceTeamMember.rejected.match(result)) {
        console.log(result.payload?.message);

        toast.error(tCommon(result.payload?.message ?? 'errors.generic'));
      }
    } catch (err) {
      console.error('Failed to remove team member:', err);
    }
  };

  const submitInvitation = async (data: FormValues) => {
    if (!currentWorkspace?.id) return;

    try {
      const result = await dispatch(
        createWorkspaceInvitation({
          workspaceId: currentWorkspace.id,
          input: {
            firstName: data.firstName.trim(),
            lastName: data.lastName.trim(),
            email: data.email.trim(),
            role: data.role,
          },
          file: pictureFile ?? undefined,
        }),
      );

      if (createWorkspaceInvitation.fulfilled.match(result) && currentWorkspace?.id) {
        toast.success(t('inviteSuccess'));
        form.reset();
        setSheetOpen(false);
        dispatch(
          fetchWorkspaceTeam({
            workspaceId: currentWorkspace.id,
            page: teamMeta?.number ?? 0,
            size: teamMeta?.size ?? 20,
          }),
        );
        dispatch(fetchUsage(currentWorkspace.id));
      }

      if (createWorkspaceInvitation.rejected.match(result)) {
        const errorMessage = result.payload?.message
          ? tCommon(result.payload.message)
          : tCommon('errors.generic');
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error('Failed to invite team member:', err);
      toast.error(tCommon('errors.generic'));
    }
  };

  const onSubmit = (data: FormValues) => runInviteExclusive(() => submitInvitation(data));

  return (
    <div className="space-y-12">
      <div className="flex items-center justify-between">
        <Title level={1}>{t('title')}</Title>

        {isAtMax ? (
          <LimitUpsellDialog />
        ) : (
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button className="text-base">
                <Plus className="size-6 mr-2 " />
                {t('addMember')}
              </Button>
            </SheetTrigger>

            <SheetContent
              side="right"
              className="lg:max-w-[784px] w-full px-6 py-8 border-none  rounded-l-[20px] bg-white"
            >
              <SheetHeader className="pb-4">
                <SheetTitle className="flex items-center justify-between">
                  <X className="size-6 cursor-pointer" onClick={() => setSheetOpen(false)} />
                  <Label className="cursor-pointer" onClick={() => setSheetOpen(false)}>
                    {t('cancel')}
                  </Label>
                </SheetTitle>
              </SheetHeader>

              <Title level={1}>{t('addMemberTitle')}</Title>

              <form className="pt-8 space-y-8" onSubmit={form.handleSubmit(onSubmit)}>
                <div className="space-y-4">
                  <Title level={2}>{t('personalData')}</Title>

                  <input
                    ref={pictureInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handlePictureSelect(file);
                      e.target.value = '';
                    }}
                  />

                  <div className="flex flex-col items-center gap-3">
                    <button
                      type="button"
                      onClick={() => pictureInputRef.current?.click()}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files?.[0];
                        if (file) handlePictureSelect(file);
                      }}
                      className="relative size-24 rounded-full overflow-hidden border-2 border-dashed border-[#C5C5CA] hover:border-primary transition-colors flex items-center justify-center bg-card cursor-pointer"
                    >
                      {picturePreview ? (
                        <img src={picturePreview} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Plus className="size-6 text-secondary" />
                      )}
                    </button>
                    <div className="text-center space-y-0.5">
                      <p className="text-sm font-medium">
                        {picturePreview ? t('picture.change') : t('picture.label')}
                      </p>
                      <p className="text-xs text-secondary">{t('picture.hint')}</p>
                    </div>
                    {picturePreview && (
                      <button
                        type="button"
                        className="text-xs text-secondary underline"
                        onClick={() => {
                          setPictureFile(null);
                          setPicturePreview(null);
                        }}
                      >
                        {t('picture.remove')}
                      </button>
                    )}
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex-1 space-y-1">
                      <Controller
                        name="lastName"
                        control={form.control}
                        render={({ field }) => (
                          <InputLabel
                            label={t('lastName')}
                            id="lastName"
                            type="text"
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      {form.formState.errors.lastName && (
                        <p className="text-xs text-error">
                          {form.formState.errors.lastName.message}
                        </p>
                      )}
                    </div>

                    <div className="flex-1 space-y-1">
                      <Controller
                        name="firstName"
                        control={form.control}
                        render={({ field }) => (
                          <InputLabel
                            label={t('firstName')}
                            id="firstName"
                            type="text"
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      {form.formState.errors.firstName && (
                        <p className="text-xs text-error">
                          {form.formState.errors.firstName.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <Title level={2}>{t('loginCredentials')}</Title>

                  <div className="space-y-1">
                    <Controller
                      name="email"
                      control={form.control}
                      render={({ field }) => (
                        <InputLabel
                          label={t('email')}
                          id="email"
                          type="email"
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                    {form.formState.errors.email && (
                      <p className="text-xs text-error">{form.formState.errors.email.message}</p>
                    )}
                  </div>
                </div>

                <p className="text-sm text-secondary">{t('inviteMessage')}</p>

                <div className="space-y-4">
                  <Title level={2}>{t('permissions')}</Title>

                  <div className="space-y-1">
                    <Controller
                      name="role"
                      control={form.control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger label={t('role')}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ADMIN">{t('admin')}</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {form.formState.errors.role && (
                      <p className="text-xs text-error">{form.formState.errors.role.message}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-center w-full">
                  <Button
                    type="submit"
                    className="text-base p-6 "
                    disabled={!isValid || isSubmitting}
                  >
                    {isSubmitting ? t('inviting') : t('inviteButton')}
                  </Button>
                </div>
              </form>
            </SheetContent>
          </Sheet>
        )}
      </div>

      <div className="space-y-4">
        <Title level={2}>{t('teamMembers')}</Title>
        <Table className="rounded-none">
          <TableHeader>
            <TableRow>
              <TableHead>{t('name')}</TableHead>
              <TableHead>{t('role')}</TableHead>
              <TableHead>{t('emailColumn')}</TableHead>
              <TableHead>{t('addDate')}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {team.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-secondary">
                  {t('noMembers')}
                </TableCell>
              </TableRow>
            ) : (
              team.map((member) => (
                <TableRow key={member.userId}>
                  <TableCell className=" flex items-center gap-4 text-sm font-normal">
                    {member.pictureUrl && (
                      <Avatar>
                        <AvatarImage src={member.pictureUrl} />
                        <AvatarFallback>
                          {member.firstName?.charAt(0) || ''} {member.lastName?.charAt(0) || ''}
                        </AvatarFallback>
                      </Avatar>
                    )}
                    {member.firstName} {member.lastName}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {member.creator && <Badge variant="finished">{t('owner')}</Badge>}

                      <Badge variant="live">{t('admin')}</Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm font-normal text-secondary">
                    {member.email}
                  </TableCell>
                  <TableCell className="text-sm font-normal text-secondary">
                    {member.invitationDate
                      ? DateTime.fromISO(member.invitationDate).toFormat('dd/MM/yyyy')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <button onClick={() => handleEditClick(member)}>
                      <EditIcon className="size-6 text-secondary" />
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog
        open={editDialogOpen}
        onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) setSelectedMember(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('editRoleTitle')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={editRoleForm.handleSubmit(onEditRoleSubmit)} className="space-y-4">
            <div className="space-y-6">
              <Controller
                name="role"
                control={editRoleForm.control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger label={t('role')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ADMIN">{t('admin')}</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              {editRoleForm.formState.errors.role && (
                <p className="text-xs text-error">{editRoleForm.formState.errors.role.message}</p>
              )}
            </div>
            <div className="flex flex-col gap-4">
              {selectedMember && !selectedMember.creator && isWelyaUser && (
                <div className="w-full">
                  <Button
                    type="button"
                    variant="outline"
                    className="text-base w-full"
                    disabled={teamActionLoading}
                    onClick={onTransferOwner}
                  >
                    {t('transferOwnership')}
                  </Button>
                </div>
              )}
              <Button
                type="submit"
                className="text-base"
                disabled={editRoleForm.formState.isSubmitting || teamActionLoading}
              >
                {editRoleForm.formState.isSubmitting || teamActionLoading
                  ? t('saving')
                  : t('saveChanges')}
              </Button>
            </div>
            <button type="button" className="w-full" onClick={handleDeleteClick}>
              {t('deleteMember')}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent transparentOverlay>
          <DialogHeader>
            <DialogTitle>
              {t('deleteConfirmTitle', {
                name: selectedMember
                  ? `${selectedMember.firstName} ${selectedMember.lastName}`
                  : '',
              })}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-secondary">{t('deleteConfirmMessage')}</p>
          <div className="flex gap-2 pt-4 w-full">
            <Button
              className="w-1/2"
              type="button"
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              {t('cancel')}
            </Button>
            <Button
              className="w-1/2"
              type="button"
              onClick={onDeleteConfirm}
              disabled={teamActionLoading}
            >
              {teamActionLoading ? t('deleting') : t('deleteConfirmButton')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
